import { forwardRef } from "react";

import { cva } from "class-variance-authority";

import { Toast as BaseUIToast } from "@base-ui/react/toast";
import { CrossIcon } from "@eisox/icons";

import { cn } from "../../index";
import type { ToastType } from "../toast.type";

/* -------------------------------------------------------------------------------------------------
 * Variants
 * -----------------------------------------------------------------------------------------------*/

const toastVariants = cva("p-0 toast-stack overflow-hidden rounded-[10px] text-base shadow-[var(--shadow-elevated)]", {
  variants: {
    type: {
      success: "bg-primary-green text-primary-white",
      error: "bg-primary-red text-primary-white",
      info: "bg-primary-blue text-primary-white",
      warning: "bg-primary-warning text-primary-dark-blue",
      loading: "bg-primary-dark-blue text-primary-white",
    },
  },
  defaultVariants: { type: "info" },
});

/* -------------------------------------------------------------------------------------------------
 * Root
 * -----------------------------------------------------------------------------------------------*/

type RootProps = BaseUIToast.Root.Props;

const Root = forwardRef<HTMLDivElement, RootProps>(({ toast, className, ...props }, forwardedRef) => (
  <BaseUIToast.Root
    {...props}
    ref={forwardedRef}
    toast={toast}
    swipeDirection={[]}
    className={cn(toastVariants({ type: toast.type as ToastType | undefined }), className)}
  />
));

Root.displayName = "Toast.Root";

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

type ContentProps = BaseUIToast.Content.Props;

const Content = forwardRef<HTMLDivElement, ContentProps>(({ className, ...props }, forwardedRef) => (
  <BaseUIToast.Content
    {...props}
    ref={forwardedRef}
    className={cn(
      "gap-2.5 p-2.5 flex items-center overflow-hidden transition-opacity duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] data-behind:opacity-0 data-expanded:opacity-100",
      className,
    )}
  />
));

Content.displayName = "Toast.Content";

/* -------------------------------------------------------------------------------------------------
 * Title
 * -----------------------------------------------------------------------------------------------*/

type TitleProps = BaseUIToast.Title.Props;

const Title = forwardRef<HTMLDivElement, TitleProps>(({ className, ...props }, forwardedRef) => (
  <BaseUIToast.Title {...props} ref={forwardedRef} className={cn("font-medium text-base", className)} />
));

Title.displayName = "Toast.Title";

/* -------------------------------------------------------------------------------------------------
 * Description
 * -----------------------------------------------------------------------------------------------*/

type DescriptionProps = BaseUIToast.Description.Props;

const Description = forwardRef<HTMLDivElement, DescriptionProps>(({ className, ...props }, forwardedRef) => (
  <BaseUIToast.Description {...props} ref={forwardedRef} className={cn("text-base", className)} />
));

Description.displayName = "Toast.Description";

/* -------------------------------------------------------------------------------------------------
 * Action
 * -----------------------------------------------------------------------------------------------*/

type ActionProps = BaseUIToast.Action.Props;

const Action = forwardRef<HTMLButtonElement, ActionProps>(({ className, ...props }, forwardedRef) => (
  <BaseUIToast.Action
    {...props}
    ref={forwardedRef}
    className={cn(
      "p-0 shrink-0 cursor-pointer border-none bg-transparent font-[inherit] text-base text-current underline",
      className,
    )}
  />
));

Action.displayName = "Toast.Action";

/* -------------------------------------------------------------------------------------------------
 * Close
 * -----------------------------------------------------------------------------------------------*/

type CloseProps = BaseUIToast.Close.Props;

const Close = forwardRef<HTMLButtonElement, CloseProps>(
  ({ className, children, "aria-label": ariaLabel, ...props }, forwardedRef) => (
    <BaseUIToast.Close
      {...props}
      ref={forwardedRef}
      aria-label={ariaLabel ?? "Close"}
      className={cn(
        "h-6 w-6 p-0 flex shrink-0 cursor-pointer items-center justify-center border-none bg-transparent text-current",
        className,
      )}
    >
      {children ?? <CrossIcon className="h-[15px] w-[15px]" />}
    </BaseUIToast.Close>
  ),
);

Close.displayName = "Toast.Close";

export { Action, Close, Content, Description, Root, Title };
export type { ActionProps, CloseProps, ContentProps, DescriptionProps, RootProps, TitleProps };
